import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import React from 'react';

/**
 * PaginationControls - Standardized Industrial Navigation Footer.
 */
const PaginationControls = ({
    totalCount,
    currentPage,
    totalPages,
    pageSize,
    onNextPage,
    onPrevPage,
    onPageSizeChange,
    hasMore,
    hasPrev,
    selectedCount = 0,
    loadingMore = false
}) => {
    // Hidden if no data
    if (totalCount === 0) return null;

    return (
        <div className="flex w-full items-center justify-between px-1 py-4 shrink-0 border-t border-slate-100 mt-2">
            {/* Status Info - Left Side */}
            <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                    <span className="text-[9px] font-black uppercase tracking-[0.15em] text-slate-400">Total</span>
                    <span className="text-[11px] font-black text-slate-900 tabular-nums">{totalCount}</span>
                </div>
                
                {selectedCount > 0 && (
                    <div className="flex items-center gap-2 animate-in fade-in slide-in-from-left-2 duration-300 bg-amber-50 px-2 py-1 rounded-md border border-amber-100">
                        <span className="text-[9px] font-black uppercase tracking-[0.15em] text-amber-600">Selected</span>
                        <span className="text-[11px] font-black text-amber-700 tabular-nums">{selectedCount}</span>
                    </div>
                )}

                <div className="text-[9px] font-black text-slate-400 uppercase tracking-[0.15em] italic">
                    Page <span className="text-slate-900 mx-1">{currentPage}</span> of <span className="text-slate-900 ml-1">{totalPages || 1}</span>
                </div>
            </div>

            {/* Navigation & Page Size - Right Side */}
            <div className="flex items-center gap-6">
                {/* Page Size Selector */}
                {onPageSizeChange && (
                    <div className="flex items-center gap-3">
                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.15em]">Rows:</span>
                        <select 
                            value={pageSize}
                            onChange={(e) => onPageSizeChange?.(Number(e.target.value))}
                            className="bg-transparent border-none text-[10px] font-black text-slate-900 uppercase tracking-widest p-0 focus:ring-0 outline-none cursor-pointer hover:text-primary transition-colors appearance-none pr-4 relative"
                            style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'currentColor\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'4\' d=\'M19 9l-7 7-7-7\' /%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right center', backgroundSize: '10px' }}
                        >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                )}

                <div className="flex items-center gap-2">
                    <button
                        onClick={onPrevPage}
                        disabled={currentPage <= 1 || loadingMore}
                        className={`group flex items-center gap-1.5 px-4 py-2 rounded-xl font-black text-[9px] uppercase tracking-[0.15em] transition-all active:scale-95 border ${
                            currentPage > 1 && !loadingMore
                            ? 'bg-white border-slate-200 text-slate-600 hover:border-primary/30 hover:bg-primary/5 hover:text-primary'
                            : 'bg-slate-50 border-slate-100 text-slate-300 cursor-not-allowed'
                        }`}
                    >
                        <ChevronLeft size={12} strokeWidth={4} className="transition-transform group-hover:-translate-x-0.5" /> Previous
                    </button>

                    <div className="w-8 text-center">
                        <span className="font-black text-[11px] text-primary">{currentPage}</span>
                    </div>

                    <button
                        onClick={onNextPage}
                        disabled={currentPage >= totalPages || loadingMore}
                        className={`group flex items-center gap-1.5 px-4 py-2 rounded-xl font-black text-[9px] uppercase tracking-[0.15em] transition-all active:scale-95 border ${
                            currentPage < totalPages && !loadingMore
                            ? 'bg-white border-slate-200 text-slate-600 hover:border-primary/30 hover:bg-primary/5 hover:text-primary'
                            : 'bg-slate-50 border-slate-100 text-slate-300 cursor-not-allowed'
                        }`}
                    >
                        Next <ChevronRight size={12} strokeWidth={4} className="transition-transform group-hover:translate-x-0.5" />
                    </button>

                    {loadingMore && (
                        <div className="ml-2 flex items-center gap-2">
                            <Loader2 size={12} className="text-primary animate-spin" />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default React.memo(PaginationControls);
